import * as qs from 'query-string';
import Cookies from 'js-cookie';
import { getFiGrowthBook } from './growthbook';
import { SizeOption } from '../types/Product';

export type QuizCtaType = 'shop' | 'trial';
export type QuizSpecies = 'dog' | 'cat';

// PDP slugs (AppPaths.Products values), not product ids.
export const QUIZ_PRODUCT_S3_PLUS = 'smart-collar-v3b';
export const QUIZ_PRODUCT_MINI = 'fi-mini';
export const QUIZ_PRODUCT_ULTRA = 'fi-ultra';

// Quiz-referred promo offer. The banner's "Up to $X off" amount is resolved
// per currency at runtime from /api/promotions/code-details (Recurly is the
// source of truth), so only the code itself lives here.
export const QUIZ_REFERRAL_COUPON_CODE = 'PERFECTFIT';

export interface QuizReferral {
  ctaType: QuizCtaType;
  recommendedProduct: string;
  species: QuizSpecies;
  petName?: string;
  sizeBand?: SizeOption;
  breed?: string;
}

export const QUIZ_REFERRAL_STORAGE_KEY = 'fi_quiz_referral';
export const QUIZ_BANNER_DISMISSED_STORAGE_KEY = 'fi_quiz_banner_dismissed';
export const QUIZ_PREVIEW_OVERRIDE_PARAM = 'quizPdpPreview';
export const QUIZ_PREVIEW_OVERRIDE_STORAGE_KEY = 'fi_quiz_pdp_preview_override';

const MAX_PET_NAME_LENGTH = 12;

/**
 * QA/preview escape hatch: `?quizPdpPreview=1` enables the personalization
 * without the GrowthBook flag (Cloudflare preview deploys can't easily get
 * the fi_gb_ee_* override cookie, which is set on the production domain).
 * Sticky for the session, like the ?quizVariant= override; `?quizPdpPreview=0`
 * clears it. Events from override sessions carry quiz_preview_override so the
 * warehouse can exclude them.
 */
export function isQuizPreviewOverrideActive() {
  try {
    const value = new URLSearchParams(window.location.search).get(QUIZ_PREVIEW_OVERRIDE_PARAM);
    if (value === '1' || value === 'true') {
      window.sessionStorage.setItem(QUIZ_PREVIEW_OVERRIDE_STORAGE_KEY, '1');
      return true;
    }
    if (value === '0' || value === 'false') {
      window.sessionStorage.removeItem(QUIZ_PREVIEW_OVERRIDE_STORAGE_KEY);
      return false;
    }
    return window.sessionStorage.getItem(QUIZ_PREVIEW_OVERRIDE_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function isQuizPdpPersonalizationEnabled() {
  return (
    isQuizPreviewOverrideActive() ||
    getFiGrowthBook().getFeatureValue<boolean>('quiz-referred-pdp-personalization', false)
  );
}

// The personalization only surfaces on the Series 3+ experience, the free-trial
// plan, and the Mini PDP — all flag-gated. On a preview deploy those flags are
// usually off, so the override also forces them on (via GrowthBook's existing
// fi_gb_ee_* override cookies) to make preview URLs self-contained. Must run
// before loadFeatures() so setForcedOverrides() picks the cookies up.
const QUIZ_PREVIEW_FORCED_FLAGS = ['series-three-plus-experience', 'free-trial-enabled', 'ecom-mini-pdp'];

export function applyQuizPreviewFlagOverrides() {
  if (!isQuizPreviewOverrideActive()) {
    return;
  }
  QUIZ_PREVIEW_FORCED_FLAGS.forEach((flag) => Cookies.set(`fi_gb_ee_${flag}`, 'true'));
}

/**
 * Pet names come from a URL param, so treat them as untrusted display input:
 * anything long or containing characters outside letters/digits/space/'-./
 * falls back to the generic species copy rather than rendering verbatim.
 */
export function sanitizeQuizPetName(raw: string | null | undefined): string | undefined {
  const name = raw?.trim();
  if (!name || name.length > MAX_PET_NAME_LENGTH) {
    return undefined;
  }
  return /^[\p{L}\p{N}][\p{L}\p{N} '.-]*$/u.test(name) ? name : undefined;
}

const QUIZ_SIZE_LABEL_TO_SIZE_OPTION: Record<string, SizeOption> = {
  'Extra Small': SizeOption.XSmall,
  Small: SizeOption.Small,
  Medium: SizeOption.Medium,
  Large: SizeOption.Large,
  'Extra Large': SizeOption.XLarge,
};

export function sizeOptionFromQuizSizeLabel(label: string | null | undefined): SizeOption | undefined {
  return label ? QUIZ_SIZE_LABEL_TO_SIZE_OPTION[label] : undefined;
}

function isSizeOption(value: string): value is SizeOption {
  return (Object.values(SizeOption) as string[]).includes(value);
}

interface BuildQuizReferralParamsArgs {
  ctaType: QuizCtaType;
  recommendedProduct: string;
  species: QuizSpecies;
  petName?: string | null;
  sizeBand?: SizeOption;
  breed?: string | null;
  /** Carry the QA preview override to the PDP so the funnel is testable before the flag ships. */
  includePreviewOverride?: boolean;
}

/**
 * The single source of the quiz→PDP handoff contract. `size` and `type` are
 * the PDP's existing preselection params (useVariantSelector/useSpeciesUrlParam);
 * everything else is namespaced `quiz_*` so it can't collide with the nano
 * referral params or other flows.
 */
export function buildQuizReferralParams({
  ctaType,
  recommendedProduct,
  species,
  petName,
  sizeBand,
  breed,
  includePreviewOverride,
}: BuildQuizReferralParamsArgs): string {
  return qs.stringify({
    quiz_ref: '1',
    quiz_cta: ctaType,
    quiz_product: recommendedProduct,
    quiz_species: species,
    quiz_pet: petName || undefined,
    quiz_breed: breed || undefined,
    // `quiz_size` is the immutable recommended band (drives the "recommended
    // size" pill); `size` seeds the initial selection but useVariantSelector
    // rewrites it as the shopper changes size, so the two must stay separate.
    quiz_size: sizeBand,
    size: sizeBand,
    type: recommendedProduct === QUIZ_PRODUCT_MINI ? species : undefined,
    [QUIZ_PREVIEW_OVERRIDE_PARAM]: includePreviewOverride ? '1' : undefined,
  });
}

function parseQuizReferralFromSearch(search: string): QuizReferral | null {
  const params = qs.parse(search);
  const single = (value: string | (string | null)[] | null | undefined) =>
    typeof value === 'string' ? value : undefined;

  if (single(params.quiz_ref) !== '1') {
    return null;
  }

  const ctaType = single(params.quiz_cta);
  const recommendedProduct = single(params.quiz_product);
  const species = single(params.quiz_species);
  if ((ctaType !== 'shop' && ctaType !== 'trial') || !recommendedProduct || (species !== 'dog' && species !== 'cat')) {
    return null;
  }

  // Read the recommended band from quiz_size (immutable), NOT size (which the
  // variant selector rewrites on selection).
  const quizSize = single(params.quiz_size);
  return {
    ctaType,
    recommendedProduct,
    species,
    petName: sanitizeQuizPetName(single(params.quiz_pet)),
    sizeBand: quizSize && isSizeOption(quizSize) ? quizSize : undefined,
    breed: single(params.quiz_breed)?.slice(0, 40) || undefined,
  };
}

function readStoredQuizReferral(): QuizReferral | null {
  try {
    const raw = window.sessionStorage.getItem(QUIZ_REFERRAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as QuizReferral) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Parses a quiz referral out of the landing URL and persists it for the rest
 * of the session (sessionStorage survives SPA navigation to checkout, so
 * Order Completed still carries the attribution). Call on PDP mount.
 */
export function captureQuizReferralFromUrl() {
  if (!isQuizPdpPersonalizationEnabled()) {
    return;
  }

  const referral = parseQuizReferralFromSearch(window.location.search);
  if (!referral) {
    return;
  }

  try {
    window.sessionStorage.setItem(QUIZ_REFERRAL_STORAGE_KEY, JSON.stringify(referral));
    window.sessionStorage.removeItem(QUIZ_BANNER_DISMISSED_STORAGE_KEY);
  } catch (e) {
    // Storage unavailable (privacy mode); personalization still works for this page view.
  }
}

export function getQuizReferral(): QuizReferral | null {
  if (!isQuizPdpPersonalizationEnabled()) {
    return null;
  }
  return parseQuizReferralFromSearch(window.location.search) ?? readStoredQuizReferral();
}

export function isQuizBannerDismissed() {
  try {
    return window.sessionStorage.getItem(QUIZ_BANNER_DISMISSED_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function dismissQuizBanner() {
  try {
    window.sessionStorage.setItem(QUIZ_BANNER_DISMISSED_STORAGE_KEY, '1');
  } catch (e) {
    // Storage unavailable; the banner will reappear on next navigation.
  }
}
